/**
 * Nombre legible de cada pantalla (el `handle.screen` de `routes.jsx`) para los avisos de las guías:
 * «Esta guía empieza en la pantalla «AI Booster»», nunca «agency_ia_booster» (QA-VIS-guias-06).
 *
 * Reusa las claves del menú, que son el nombre que el usuario ya conoce. Una pantalla sin entrada
 * acá no se nombra en el aviso (ver `navigateHint` en `GuideRunner`). Si marcás un ancla en una
 * pantalla nueva, sumala.
 */
const SCREEN_LABEL_KEYS = {
    dashboard: 'guides.screen_home',
    marketing: 'sidebar_v2.dashboard',
    agency_clients: 'sidebar_v2.clients',
    agency_ia_booster: 'sidebar_v2.ia_booster',
    informes: 'sidebar_v2.informes',
    data_explorer: 'sidebar_v2.data_explorer',
    agency_alerts: 'sidebar_v2.alerts',
    alerts_center: 'sidebar_v2.alerts_center',
    alerts_uncontrolled: 'alerts_center.tabs.uncontrolled',
    // `client_default` NO va: en el clásico esa ruta es Monitoreo, no el Panel (review C-rec-guias-09).
    client_control_central: 'client_tabs.control_central',
    // Las pantallas de las guías «Mostrarme cómo» de Aprende Dashbo.
    client_lines: 'client_tabs.subnav_control_lines',
    client_booster: 'client_tabs.ia_booster',
    client_portal_config: 'client_tabs.portal',
    agency_team: 'sidebar_v2.team',
};

export function screenLabelKey(screen) {
    return (screen && SCREEN_LABEL_KEYS[screen]) || null;
}

/**
 * Rutas distintas que dibujan la MISMA pantalla: para una guía son una sola (contrato §3 de la tanda
 * 2026-10-02-recorrido-ux21). Una guía —y cada paso— guarda UNA pantalla, y sin esto el recorrido
 * del Tablero armado sobre `marketing` no salía a quien entra por `/`.
 *   - `/` (`dashboard`) dibuja el Tablero igual que `/marketing` SÓLO con el módulo de marketing
 *     (`Default.js`); sin él, `/` es el listado de cuentas y el recorrido del Tablero no puede salir
 *     ahí (review C-rec-guias-05).
 *   - En la 2.1, `/client/:id` (`client_default`) abre el Panel igual que `/client/:id/control-central`
 *     (`ClientDefaultRoute`). En el clásico abre Monitoreo: ahí NO son la misma.
 *   - Informes nuevo (`informes`) y un informe guardado (`informe_editor`) son el MISMO editor: las
 *     cuatro rutas montan `InformesPage` y guardar un informe nuevo sólo reemplaza la URL, sin remontar
 *     (rule `informes-navegacion.md`). En los dos marcos. Sin esto, una guía que pide guardar o abrir un
 *     informe quedaba «en otra pantalla» a mitad de camino (Aprende Dashbo, apr-guias).
 *   - «Inversión sin control» (`alerts_uncontrolled`, `/alerts/sin-control`) es una solapa del Centro de
 *     Alertas (`alerts_center`) SÓLO con el Centro: ahí se dibuja con las otras solapas y la guía del
 *     Centro puede arrancar en ella (el correo diario abre esa solapa). Sin el Centro la solapa se ve
 *     sola, sin las solapas que son el ancla fija de esa guía, así que NO son la misma pantalla (UD-5).
 * La primera del grupo es la que se usa como clave (el tope de una automática por pantalla).
 *
 * El «marco» (`frame`) es `{uiVersion, dashboardIsTablero, alertsCenter}` (lo arma `GuidesProvider`), o
 * el `uiVersion` a secas: sin `dashboardIsTablero` se asume el Tablero, que es el caso común; sin
 * `alertsCenter`, que no hay Centro (fail-closed).
 */
const SAME_SCREEN_GROUPS = [
    {screens: ['marketing', 'dashboard'], applies: (frame) => frame.dashboardIsTablero !== false},
    {screens: ['client_control_central', 'client_default'], applies: (frame) => frame.uiVersion === 'v2'},
    {screens: ['informes', 'informe_editor'], applies: () => true},
    {screens: ['alerts_center', 'alerts_uncontrolled'], applies: (frame) => frame.alertsCenter === true},
];

export function frameOf(frame) {
    return frame && typeof frame === 'object' ? frame : {uiVersion: frame};
}

/** La pantalla tal como la cuentan las guías: la primera de su grupo, o ella misma. */
export function guideScreenKey(screen, frame) {
    if (!screen) return screen;
    const group = SAME_SCREEN_GROUPS.find((g) => g.screens.includes(screen) && g.applies(frameOf(frame)));
    return group ? group.screens[0] : screen;
}

export function sameGuideScreen(a, b, frame) {
    return !!a && !!b && guideScreenKey(a, frame) === guideScreenKey(b, frame);
}

/**
 * El texto del aviso «ve a la pantalla X» (arranque manual y vista previa). Nombra la pantalla como la
 * ve el usuario, nunca con su id técnico (QA-VIS-guias-06). Si no tiene nombre conocido, el aviso
 * manual no la nombra; la vista previa (sólo superadmins, que arman la guía) suma el id para que se
 * pueda corregir.
 */
export function navigateHint(t, screen, index, isPreview) {
    const labelKey = screenLabelKey(screen);
    const opts = {n: index + 1, interpolation: {escapeValue: false}};
    if (isPreview) {
        const name = labelKey ? `${t(labelKey)} (${screen})` : screen;
        return t('guides.preview_navigate', {...opts, screen: name});
    }
    if (!labelKey) return t('guides.manual_navigate_unknown');
    return t('guides.manual_navigate', {...opts, screen: t(labelKey)});
}
